/**
 * Google Analytics 4 Service
 * Provides page view tracking and custom event logging
 * 
 * Note: GA is only initialized after user gives cookie consent.
 * See CookieConsent.js for the consent flow.
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Check if GA is enabled and available (only after consent + script loaded)
const isGAEnabled = () => {
    return GA_MEASUREMENT_ID && 
           GA_MEASUREMENT_ID !== '' && 
           !GA_MEASUREMENT_ID.includes('XXXXXXXXXX') &&
           typeof window !== 'undefined' && 
           typeof window.gtag === 'function';
};

/**
 * Track a page view
 * @param {string} path - The page path (e.g., '/dashboard')
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
    if (!isGAEnabled()) {
        return;
    }

    window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
        page_location: window.location.href,
    });
};

/**
 * Track a custom event
 * @param {string} eventName - The event name (e.g., 'wallet_connected', 'address_added')
 * @param {Object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
    if (!isGAEnabled()) {
        return;
    }

    window.gtag('event', eventName, params);
};

/**
 * Track user login
 * @param {string} method - The login method (e.g., 'email', 'google', 'wallet')
 */
export const trackLogin = (method) => {
    trackEvent('login', { method });
};

/**
 * Track user signup
 * @param {string} method - The signup method (e.g., 'email', 'google', 'wallet')
 */
export const trackSignUp = (method) => {
    trackEvent('sign_up', { method });
};

/**
 * Track address added to watchlist
 * @param {string} chain - The blockchain (e.g., 'ethereum', 'base')
 * @param {string} source - How it was added (e.g., 'manual', 'wallet', 'import')
 */
export const trackAddressAdded = (chain, source = 'manual') => {
    trackEvent('address_added', {
        chain: chain,
        source: source,
    });
};

/**
 * Track alert created
 * @param {string} alertType - The alert type (e.g., 'balance', 'transaction', 'approval')
 * @param {string} chain - The blockchain
 */
export const trackAlertCreated = (alertType, chain) => {
    trackEvent('alert_created', {
        alert_type: alertType,
        chain: chain,
    });
};

/**
 * Track Slack integration
 * @param {string} action - The action (e.g., 'connected', 'disconnected')
 */
export const trackSlackIntegration = (action) => {
    trackEvent('slack_integration', {
        action: action,
    });
};

/**
 * Track errors for debugging
 * @param {string} errorType - The error type
 * @param {string} errorMessage - The error message
 * @param {string} component - Where the error occurred
 */
export const trackError = (errorType, errorMessage, component = 'unknown') => {
    trackEvent('error', {
        error_type: errorType,
        error_message: errorMessage?.substring(0, 100), // Limit length
        component: component,
    });
};

/**
 * Set user properties (for authenticated users)
 * @param {Object} properties - User properties
 */
export const setUserProperties = (properties) => {
    if (!isGAEnabled()) return;
    
    window.gtag('set', 'user_properties', properties);
};

/**
 * Set user ID for cross-device tracking
 * @param {string} userId - The user's unique ID
 */
export const setUserId = (userId) => {
    if (!isGAEnabled()) return;
    
    window.gtag('set', { user_id: userId });
};

const analyticsService = {
    trackPageView,
    trackEvent,
    trackLogin,
    trackSignUp,
    trackAddressAdded,
    trackAlertCreated,
    trackSlackIntegration,
    trackError,
    setUserProperties,
    setUserId,
};

export default analyticsService;

